import { useEffect, useRef, useState } from "react";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import { getLocale, history, shallowEqual, useSelector } from "umi";
import moment from "moment";
import { downLoadPcConsoleV3DayPerformanceTable, getPcConsoleV3DayPerformanceTable } from "@/pages/dashboard/services/overview";
import { LANGUAGE_MAP, PERIODTIMEOBJ } from "@/pages/dashboard/_config/constant";
import { IsSingleDay } from "@/pages/dashboard/utils/utils";
import { PagingQuery } from "tea-component";
import { getTableRequestParams } from "./hooks";
import { getMultiSortParams, mergeCustomAccumulationKeys, mergeCustomFiltersFromOtherParams, mergeDataSourceCustomAccumulationKeys, overallPlaytimeFormatter } from "./utils";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";
import { SortBy } from "tea-component/lib/table/addons";
import { IConfig, ICustomSelectorDimensionConfig, PerformanceTableReqParams, TLockColumn } from "./types";

const useData = (
  filterValue: any,
  filterLoading: boolean,
  selectType: string,
  config: IConfig,
  componentMetrics: Array<ComponentMetricsItemType>,
  otherParams?: any,
  lockColumns?: TLockColumn[],
  columns = [] as any[],
  isUseNewSortParams?: boolean,
  /**
   * 需要写入请求参数 accumulation.custom_accumulation 的维度 key 列表
   * - 用于宽表自定义分组（如 ad_format）
   */
  includeCustomAccumulationKeys?: string[],
  /**
   * customSelectorsDimensions 配置，用于在选「全部」时按 isAccumulationWhenAllSelected 决定 is_accumulation
   */
  customSelectorsDimensions?: ICustomSelectorDimensionConfig[],
  dataCustomFiltersRequestKey?: string,
) => {
  const { api: apiObj, formatData, isHaveDiff = false, useTime = false, formatType = '', dimensionConfig,
    dateFilterType = 'rangeTime', defaultSort = [], getParams } = config || {};
  const { defaultAccumulation = ['date'], isAutoSort } = dimensionConfig || {};
  const customFiltersFromData = config?.customFiltersConfig?.customFiltersFromData;
  const historyParam: any = history?.location?.state;
  const urlOtherParams= historyParam ? JSON.parse(historyParam)?.otherParams : undefined;
 
  const { oneTimeDefaultSort } = urlOtherParams || {};
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // // 排序
  // 只有进入页面第一次排序生效，但是区别于defaultSort，相当于进入页面点击了一个排序
  const oneTimeDefaultSortRef = useRef(true);
  const [sort, setSort] = useState<SortBy[]>(defaultSort);
  // 总条数
  const [total, setTotal] = useState(0);
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const { overLimitBarTriggerKey, handleResponse } = useOverLimitTrigger();

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);

  // const [,forceUpdate] = useReducer(x=>x+1,0)
  const filterValueSort = useRef(filterValue);
  const accumulateOldValueRef = useRef(defaultAccumulation);

  const gameCode = useSelector((state: any) => state?.dashboard?.currentGame?.game_code, shallowEqual);

  const metricsList = componentMetrics?.map((item: { reqMetricKey: any; }) => item?.reqMetricKey) || [];
  const otherParamsRef = useRef(otherParams);
  otherParamsRef.current = otherParams;

  // 获取表格上传参数
  const getRequestParams = (paging: any, sort: any, filterValue: any, otherParams?: any) => {
    // overview页面的日期上传参数逻辑与其他页面不同, 故特殊处理
    // eslint-disable-next-line prefer-const
    let { rangeTime: [startTime, endTime] = [], diff, timeType, singleDaily } = filterValue || {};
    const periodObj = { period: PERIODTIMEOBJ?.[timeType] || 'daily' };
    /**
     * day 少于七天补足七天
     * week 少于六周补足六周
     * month 少于六月补足六月
     */
    if (diff && isHaveDiff) {
      switch (timeType) {
        case "Day": {
          startTime = moment(startTime).subtract(7, 'day').format('YYYY-MM-DD');
          break;
        }
        case "Week": {
          startTime = moment(endTime).week(moment(endTime).week() - 5).startOf('isoWeek').format('YYYY-MM-DD');
          break;
        }
        case "Month": {
          startTime = moment(endTime).month(moment(endTime).month() - 5).startOf('month').format('YYYY-MM-DD');
          break;
        }
      }
    }

    const accumulated = accumulateOldValueRef?.current;

    const baseParams: PerformanceTableReqParams = getTableRequestParams({
      gameCode, filterValue,
      accumulated,
      sort,
      paging,
      selectType,
      start_date: moment(startTime).format("YYYYMMDD"),
      end_date: moment(endTime).format("YYYYMMDD"),
      metrics: metricsList || [],
      ...otherParams,
      ...periodObj,
    });

    // 将配置的维度 key 写入 accumulation.custom_accumulation（与历史「维度勾选」能力保持一致）
    const paramsWithAccumulation = mergeCustomAccumulationKeys(baseParams, includeCustomAccumulationKeys, {
      customFiltersMerge: otherParams?.custom_filters_merge,
      selectorDimensions: customSelectorsDimensions,
    });

    const paramsWithDataSourceAccumulation = mergeDataSourceCustomAccumulationKeys(
      paramsWithAccumulation,
      accumulated,
      customFiltersFromData,
    );

    // 将 custom_filters_merge（如 selector 选中值）合并进 filters.custom_filters，不随请求发送 custom_filters_merge 字段
    const params = mergeCustomFiltersFromOtherParams(paramsWithDataSourceAccumulation, otherParams);

    // Benchmark默认倒序
    if (sort?.[0]?.isDefaultDesc) {
      params.desc = true;
    }

    // 单日筛选器
    if (dateFilterType === 'singleDaily') {
      params.start_date = singleDaily;
      params.end_date = singleDaily;
    }

    // 使用start_time和end_time作为时间参数（return页面宽表参数不统一）
    if (useTime) {
      delete params.start_date;
      delete params.end_date;
      params.start_time = moment(startTime).format("YYYY-MM-DD");
      params.end_time = moment(endTime).format("YYYY-MM-DD");
    }
    return params;
  };

  useEffect(() => {
    filterValueSort.current = { filterValue, paging };
  }, [filterValue, paging, sort]);

  // 点击筛选器的时候
  useEffect(() => {
    if (filterLoading) return;
    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
    let newSort = (oneTimeDefaultSortRef?.current && metricsList?.includes(oneTimeDefaultSort?.[0]?.by)) ?  (oneTimeDefaultSort || defaultSort) : defaultSort;

    // 自动按照第一列排序
    if (isAutoSort && accumulateOldValueRef?.current?.length > 0) {
      newSort = [{ "by": accumulateOldValueRef.current[0], "order": undefined }];
    } else {
      oneTimeDefaultSortRef.current = false;
      setSort(newSort);
    }
    const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, newSort, filterValue,
      {
        ...otherParamsRef.current,
        multi_sort: getMultiSortParams(lockColumns, columns, newSort?.[0], selectType, isUseNewSortParams),
      }
    );
    getTableData(params);

  }, [filterValue, filterLoading, dataCustomFiltersRequestKey]);

  const formatDataMap: any = {
    'playtime': overallPlaytimeFormatter
  };

  const getTableData = (params: any) => {
    setIsError(false);
    setLoading(true);
    const api = `${apiObj?.tableApi}?language=${LANGUAGE_MAP[getLocale()]}`;
    if (typeof getParams === 'function') {
      params = getParams(params);
    }
    getPcConsoleV3DayPerformanceTable(api, params).then((res) => {
      setLoading(false);
      const data = formatDataMap?.[formatType] ? formatDataMap?.[formatType](params, res) : formatData ? formatData(params, res) : res;
      setTableData(data?.metrics_list || []);
      setStatisticsData(data?.aggregation || []);
      // 设置总条数
      setTotal(res.row_count);
      setFrontLogicalFlag(res?.front_logical_flag);
      handleResponse(res?.front_logical_flag);
    }).catch(e => {
      setLoading(false);
      setIsError(true);
      console.log("overview 宽表请求错误: ", filterValue.timeType, e);
    });
  };

  const setSortFunc = (sort: SortBy[]) => {
    oneTimeDefaultSortRef.current = false;
    setSort(sort);
  };


  return {
    tableData,
    statisticsData,
    paging,
    loading,
    isError,
    sort,
    total,
    frontLogicalFlag,
    overLimitBarTriggerKey,
    filterValueSort,
    accumulateOldValueRef,
    isSingle: IsSingleDay({ ...filterValue, period: PERIODTIMEOBJ[filterValue?.timeType] }),
    setPaging,
    setSort: setSortFunc,
    getRequestParams,
    getData: getTableData,
    downLoadData: downLoadPcConsoleV3DayPerformanceTable
  };
};

export default useData;